import { cacheInvalidate } from './apiCache';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

export interface EnterpriseContact {
  role: string;
  name: string;
  email: string;
  title?: string | null;
}

export interface EnterpriseProfile {
  id: number;
  user_id: number;
  company_name: string | null;
  jurisdiction: string | null;
  years_in_operation: string | null;
  mac_id: string | null;
  gst_number: string | null;
  founder_names: string[] | null;
  num_members: string | null;
  existing_policies: string[] | null;
  products_services: string | null;
  other_jurisdictions: string[] | null;
  new_jurisdictions: string[] | null;
  business_model: string | null;
  industry_type: string | null;
  parent_company: string | null;
  subsidiary_companies: string[] | null;
  company_hierarchy: { name: string; type: string }[] | null;
  onboarding_completed: boolean;
  // Consolidated from company_profile
  company_size: string | null;
  headquarters_location: string | null;
  ai_usage_declaration: string[] | null;
  processes_personal_data: boolean | null;
  regulatory_declarations: string[] | null;
  contacts: EnterpriseContact[] | null;
  // Admin-managed fields
  max_seats: number | null;
  amount_paid: number | null;
  amount_pending: number | null;
  admin_notes: string | null;
}

export interface EnterpriseOnboardingPayload {
  company_name: string;
  jurisdiction: string;
  years_in_operation: string;
  mac_id?: string;
  gst_number?: string;
  founder_names?: string[];
  num_members?: string;
  existing_policies?: string[];
  products_services?: string;
  other_jurisdictions?: string[];
  new_jurisdictions?: string[];
  business_model?: string;
  industry_type?: string;
  parent_company?: string;
  subsidiary_companies?: string[];
  company_hierarchy?: { name: string; type: string }[];
  // Consolidated from company_profile
  company_size?: string;
  headquarters_location?: string;
  ai_usage_declaration?: string[];
  processes_personal_data?: boolean;
  regulatory_declarations?: string[];
  contacts?: EnterpriseContact[];
}

export interface CountryOption { id: string; name: string; iso2: string; }
export interface JurisdictionOption { id: string; name: string; code: string; type: string; }

export async function fetchCountries(): Promise<CountryOption[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/countries`);
  const data = await res.json();
  return data.countries ?? [];
}

/** One jurisdiction with its country's name, for the type-ahead. */
export interface JurisdictionChoice { country: string; name: string; type: string; }

export async function fetchJurisdictionChoices(): Promise<JurisdictionChoice[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/jurisdiction-options`);
  const data = await res.json();
  return data.options ?? [];
}

export async function fetchJurisdictionsByCountry(countryId: string): Promise<JurisdictionOption[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/jurisdictions?country_id=${encodeURIComponent(countryId)}`);
  const data = await res.json();
  return data.jurisdictions ?? [];
}


export async function fetchEnterpriseProfile(): Promise<EnterpriseProfile> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/profile`, { headers: authHeaders() });
  return parseResponse<EnterpriseProfile>(res);
}

function invalidateEnterpriseCaches(): void {
  cacheInvalidate('enterprise-profile');
  cacheInvalidate('company-profile');
  cacheInvalidate('repo-summary');
}

export async function submitEnterpriseOnboarding(payload: EnterpriseOnboardingPayload): Promise<EnterpriseProfile> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/onboarding`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponse<EnterpriseProfile>(res);
  invalidateEnterpriseCaches();
  return data;
}

export async function updateEnterpriseProfile(payload: EnterpriseOnboardingPayload): Promise<EnterpriseProfile> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/profile`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponse<EnterpriseProfile>(res);
  invalidateEnterpriseCaches();
  return data;
}

// ── Team Members ──────────────────────────────────────────────────────────────

export type FeatureKey = 'overview' | 'search' | 'chat' | 'regulations' | 'newsletters' | 'charts' | 'map' | 'chat-history' | 'search-history' | 'comparison' | 'asset-repository' | 'governance' | 'team';

export const ALL_FEATURES: FeatureKey[] = ['overview', 'search', 'chat', 'chat-history', 'regulations', 'newsletters', 'charts', 'map', 'search-history', 'comparison', 'asset-repository', 'governance', 'team'];

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  overview: 'Overview',
  'chat-history': 'Chat History',
  'search-history': 'Search History',
  search: 'Search',
  chat: 'AI Chat',
  regulations: 'Regulations',
  newsletters: 'Newsletters',
  charts: 'Charts & Stats',
  map: 'Global Map',
  comparison: 'Comparison',
  'asset-repository': 'Asset Repository',
  governance: 'Governance',
  team: 'Team Access',
};

export interface TeamMember {
  id: number;
  name: string;
  employee_id: string | null;
  email: string;
  position: string | null;
  allowed_features: FeatureKey[];
  is_active: boolean;
  created_at: string | null;
  linked_user_id?: number | null;
  login_enabled?: boolean;
  invite_email_sent?: boolean;
  temporary_password?: string | null;
  /** Assigned role, or null when the member uses their own feature list. */
  role_id?: number | null;
  /**
   * What the member can actually use. When a role is assigned this comes from
   * the role, not from `allowed_features` — the two routinely differ, and only
   * this one reflects reality. Always prefer it for display.
   */
  effective_features?: FeatureKey[];
}

export interface TeamMemberPayload {
  name: string;
  employee_id?: string;
  email: string;
  position?: string;
  allowed_features?: FeatureKey[];
}

export async function listTeamMembers(): Promise<TeamMember[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/team`, { headers: authHeaders() });
  return parseResponse<TeamMember[]>(res);
}

export async function addTeamMember(payload: TeamMemberPayload): Promise<TeamMember> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/team`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<TeamMember>(res);
}

export async function updateTeamMember(id: number, payload: Partial<TeamMemberPayload> & { is_active?: boolean }): Promise<TeamMember> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/team/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<TeamMember>(res);
}

export async function resetTeamMemberPassword(id: number): Promise<TeamMember> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/team/${id}/reset-password`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return parseResponse<TeamMember>(res);
}

/** Activate, deactivate or remove several members at once (SRS #79). */
export async function bulkTeamAction(member_ids: number[], action: 'activate' | 'deactivate' | 'remove'):
  Promise<{ action: string; done: number[]; skipped: { id: number; reason: string }[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/team/bulk`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ member_ids, action }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}

export async function deleteTeamMember(id: number): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/team/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
}

// ── Roles (SRS #4, #69) ───────────────────────────────────────────────────────
// A role is a named bundle of features. Assigning one governs the member's
// access; clearing it returns them to their own `allowed_features`.

export interface EnterpriseRole {
  id: number;
  name: string;
  description: string | null;
  allowed_features: FeatureKey[];
  /** Predefined roles are editable but cannot be deleted. */
  is_system: boolean;
}

export async function listRoles(): Promise<EnterpriseRole[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/roles`, { headers: authHeaders() });
  return parseResponse<EnterpriseRole[]>(res);
}

export async function createRole(payload: {
  name: string; allowed_features: FeatureKey[]; description?: string;
}): Promise<EnterpriseRole> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/roles`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<EnterpriseRole>(res);
}

export async function updateRole(id: number, payload: {
  name?: string; allowed_features?: FeatureKey[]; description?: string;
}): Promise<EnterpriseRole> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/roles/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<EnterpriseRole>(res);
}

export async function deleteRole(id: number): Promise<{ deleted: boolean; members_unassigned: number }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/enterprise/roles/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return parseResponse<{ deleted: boolean; members_unassigned: number }>(res);
}

/**
 * Assign a role to a member, or pass null to clear it.
 *
 * `role_id` is a query parameter, not a body field — that is what the endpoint
 * accepts, so sending it as JSON would silently clear the role instead.
 */
export async function assignMemberRole(
  memberId: number, roleId: number | null,
): Promise<{ member_id: number; role_id: number | null; effective_features: FeatureKey[] }> {
  const query = roleId === null ? '' : `?role_id=${roleId}`;
  const res = await fetch(
    `${API_BASE_URL}/api/v1/enterprise/team/${memberId}/role${query}`,
    { method: 'POST', headers: authHeaders() },
  );
  return parseResponse<{ member_id: number; role_id: number | null; effective_features: FeatureKey[] }>(res);
}
